import React, { useState, useEffect } from 'react';
import { Tag, Check, Sparkles, ShieldAlert, Settings, RefreshCw } from 'lucide-react';
import { CustomProject } from '../../types/productBuilder';
import { useAuth } from '../../lib/AuthContext';

interface BundleProps {
  project: CustomProject;
  onUpdateProject: (p: CustomProject) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Bundle: React.FC<BundleProps> = ({ project, onUpdateProject, onAddLog }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const isApplied = (project as any).bundleApplied === true;

  const [quote, setQuote] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [showAdminPanel, setShowAdminPanel] = useState<boolean>(false);

  // Editable fields for admin
  const [adminSetup, setAdminSetup] = useState<number>(399.00);
  const [adminPromoSetup, setAdminPromoSetup] = useState<number>(239.00);
  const [adminHosting, setAdminHosting] = useState<number>(29.99);
  const [adminEmail, setAdminEmail] = useState<number>(12.00);
  const [adminDomain, setAdminDomain] = useState<number>(4.50);
  
  const [adminVatRegistered, setAdminVatRegistered] = useState<boolean>(false);
  const [adminVat, setAdminVat] = useState<number>(20);
  const [adminPricesIncludeVat, setAdminPricesIncludeVat] = useState<boolean>(false);
  const [adminVatRegNum, setAdminVatRegNum] = useState<string>('GB123456789');
  const [adminEffectiveDate, setAdminEffectiveDate] = useState<string>('2026-06-20');

  const fetchQuote = async () => {
    setLoading(true);
    try {
      const idToken = user ? await user.getIdToken() : '';
      const res = await fetch('/api/product-builder/bundle/quote', {
        headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {}
      });
      const data = await res.json();
      if (data.success && data.quote) {
        setQuote(data.quote);
        setAdminSetup(data.quote.originalSetupPrice);
        setAdminPromoSetup(data.quote.discountedSetupPrice);
        setAdminHosting(data.quote.hostingPrice);
        setAdminEmail(data.quote.emailPrice);
        setAdminDomain(data.quote.domainPrice);
        setAdminVat(data.quote.vatRate);
        setAdminVatRegistered(data.quote.isVatRegistered);
        setAdminPricesIncludeVat(data.quote.pricesIncludeVat);
        setAdminVatRegNum(data.quote.vatRegistrationNumber);
        setAdminEffectiveDate(data.quote.effectiveDate);
      }
    } catch (err) {
      console.error("Failed to load bundle quote from backend", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const idToken = user ? await user.getIdToken() : '';
        const res = await fetch('/api/product-builder/user-role', {
          headers: {
            'Authorization': idToken ? `Bearer ${idToken}` : '',
            'X-CSRF-Token': document.cookie.match(/(^|;)\s*csrf_token_secret\s*=\s*([^;]+)/)?.[2] || ''
          }
        });
        const data = await res.json();
        if (data.success && data.role === 'admin') {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (e) {
        console.error("Failed to fetch user role", e);
        setIsAdmin(false);
      }
    };
    if (user?.uid) {
      fetchUserRole();
    } else {
      setIsAdmin(false);
    }
  }, [user?.uid, user?.email]);

  useEffect(() => {
    fetchQuote();
  }, [user]);

  const saveAdminPricing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      alert("UNAUTHORIZED: Sign in or authenticate first to update pricing.");
      return;
    }
    setUpdating(true);
    try {
      const csrfToken = document.cookie.match(/(^|;)\s*csrf_token_secret\s*=\s*([^;]+)/)?.[2] || '';
      const idToken = await user.getIdToken();
      const res = await fetch('/api/admin/product-builder/pricing-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          setupPrice: adminSetup,
          discountedSetupPrice: adminPromoSetup,
          hostingMonthly: adminHosting,
          emailMonthly: adminEmail,
          domainMonthly: adminDomain,
          vat_registered: adminVatRegistered,
          vat_rate: adminVat,
          prices_include_vat: adminPricesIncludeVat,
          vat_registration_number: adminVatRegNum,
          effective_date: adminEffectiveDate
        })
      });
      const data = await res.json();
      if (data.success) {
        onAddLog('Product Builder', 'Updated global product bundle pricing configurations', 'SUCCESS', 'Admin overwritten baseline configurations values.');
        alert("Pricing configuration successfully updated and saved server-side!");
        setShowAdminPanel(false);
        fetchQuote();
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Update failed: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const applyBundle = () => {
    onUpdateProject({
      ...project,
      bundleApplied: true
    } as any);

    onAddLog('Product Builder', 'Locked Connected Launch Bundle onto draft', 'SUCCESS', 'Onboarding standard package discount mapped.');
    alert(`✨ CONNECTED LAUNCH BUNDLE APPLIED!\n\nStandard setup fees reduced from £${quote?.originalSetupPrice || '399'} down to £${quote?.discountedSetupPrice || '239.00'} (one-off), incorporating managed domain deployment, hosting setups, secure DNS setups and £${quote?.originalSetupPrice - quote?.discountedSetupPrice} savings.`);
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-zinc-500 font-mono flex items-center justify-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin text-[#e5c158]" />
        <span>Syncing dynamic bundle pricing parameters...</span>
      </div>
    );
  }

  // Calculate You Save value safely
  const standardSetup = Number(quote?.originalSetupPrice || 399);
  const promoSetup = Number(quote?.discountedSetupPrice || 239);
  const youSaveVal = standardSetup - promoSetup;

  return (
    <div className="npx-builder-bundle-container space-y-6 text-left animate-fade-in font-sans">
      
      {/* HEADER SECTION */}
      <div className="border-b border-white/5 pb-4 flex flex-wrap justify-between items-end gap-3 font-mono">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Tag className="w-4 h-4 text-[#e5c158]" />
            <span className="bg-[#e5c158] text-black text-[7.5px] font-sans font-black uppercase px-2 py-0.5 rounded leading-none">
              Onboarding Special
            </span>
            <h2 className="text-sm font-mono tracking-widest text-[#e5c158] uppercase font-black">NEXORAX Connected Launch Bundle Pricing</h2>
          </div>
          <p className="text-xs text-zinc-400 font-sans normal-case leading-normal">
            Lock direct rollouts including Cloud Hosting, professional email, and domain management with absolute tax and discount transparency.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              className="px-3.5 py-2 hover:bg-zinc-800 border border-white/5 rounded-xl text-[10px] uppercase font-bold flex items-center gap-1 text-white cursor-pointer"
            >
              <Settings className="w-3.5 h-3.5 text-zinc-400" />
              <span>Admin Console</span>
            </button>
          )}

          {!isApplied ? (
            <button
              onClick={applyBundle}
              className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-400 hover:to-yellow-500 text-black font-semibold rounded-xl text-[10px] font-mono uppercase font-black tracking-wider shadow-[0_0_15px_rgba(229,193,88,0.15)] transition-all cursor-pointer flex items-center gap-1"
            >
              <Sparkles className="w-3.5 h-3.5 text-black" />
              <span>Lock Introductory Bundle Rate</span>
            </button>
          ) : (
            <span className="px-4 py-2 border border-[#00ff00]/20 bg-[#00ff00]/5 text-[#ffc526] font-semibold text-[10px] font-mono uppercase rounded-xl flex items-center gap-1.5">
              <Check size={12} className="text-[#00ff00]" />
              <span>Connected Launch Locked IN</span>
            </span>
          )}
        </div>
      </div>

      {/* ADMIN CONFIGURATOR COMPONENT */}
      {showAdminPanel && isAdmin && (
        <form onSubmit={saveAdminPricing} className="p-6 rounded-2xl bg-zinc-950/90 border border-amber-500/30 space-y-4 font-mono animate-slide-in text-white">
          <div className="flex items-center gap-2 border-b border-white/5 pb-2">
            <Settings size={14} className="text-amber-500" />
            <h3 className="text-xs font-bold text-white uppercase tracking-widest">Pricing Configuration Console (Admin Mode)</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-[9px] text-zinc-450 block uppercase font-bold">Standard Setup Price (£):</label>
              <input
                type="number"
                step="0.01"
                required
                value={adminSetup}
                onChange={(e) => setAdminSetup(Number(e.target.value))}
                className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-zinc-450 block uppercase font-bold">Introductory Price (£):</label>
              <input
                type="number"
                step="0.01"
                required
                value={adminPromoSetup}
                onChange={(e) => setAdminPromoSetup(Number(e.target.value))}
                className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-zinc-450 block uppercase font-bold">Cloud Hosting Monthly (£):</label>
              <input
                type="number"
                step="0.01"
                required
                value={adminHosting}
                onChange={(e) => setAdminHosting(Number(e.target.value))}
                className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-zinc-450 block uppercase font-bold">Managed Email Monthly (£):</label>
              <input
                type="number"
                step="0.01"
                required
                value={adminEmail}
                onChange={(e) => setAdminEmail(Number(e.target.value))}
                className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-zinc-450 block uppercase font-bold">Domain Monthly (£):</label>
              <input
                type="number"
                step="0.01"
                required
                value={adminDomain}
                onChange={(e) => setAdminDomain(Number(e.target.value))}
                className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-zinc-450 block uppercase font-bold">VAT Rate (%):</label>
              <input
                type="number"
                step="0.1"
                required
                value={adminVat}
                onChange={(e) => setAdminVat(Number(e.target.value))}
                className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
              />
            </div>
          </div>

          {/* ADVANCED VAT SECURITY MODULES */}
          <div className="p-4 bg-black/60 rounded-xl border border-zinc-800 space-y-3">
            <h4 className="text-[10px] font-bold text-yellow-500 uppercase tracking-wider">Dynamic VAT Controls</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-xs select-none">
                <input
                  type="checkbox"
                  checked={adminVatRegistered}
                  onChange={(e) => setAdminVatRegistered(e.target.checked)}
                  className="rounded border-zinc-800 text-yellow-500 bg-black"
                />
                <span className="text-[11px] text-zinc-300 uppercase font-black">VAT Registered</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer text-xs select-none">
                <input
                  type="checkbox"
                  checked={adminPricesIncludeVat}
                  onChange={(e) => setAdminPricesIncludeVat(e.target.checked)}
                  className="rounded border-zinc-800 text-yellow-500 bg-black"
                />
                <span className="text-[11px] text-zinc-300 uppercase font-black">Prices Include VAT</span>
              </label>

              <div className="space-y-1">
                <span className="text-[9px] text-zinc-450 block uppercase font-bold">VAT Reg Number:</span>
                <input
                  type="text"
                  required
                  value={adminVatRegNum}
                  onChange={(e) => setAdminVatRegNum(e.target.value)}
                  className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
                />
              </div>

              <div className="space-y-1">
                <span className="text-[9px] text-zinc-450 block uppercase font-bold">Effective Date:</span>
                <input
                  type="date"
                  required
                  value={adminEffectiveDate}
                  onChange={(e) => setAdminEffectiveDate(e.target.value)}
                  className="w-full bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 text-[9px] uppercase font-bold pt-2">
            <button
              type="button"
              onClick={() => setShowAdminPanel(false)}
              className="px-3 py-1.5 bg-zinc-900 border border-white/5 rounded text-zinc-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updating}
              className="px-4 py-1.5 bg-amber-500 rounded text-black font-extrabold flex items-center gap-1 cursor-pointer hover:bg-amber-400"
            >
              {updating && <RefreshCw size={10} className="animate-spin" />}
              <span>Save & Update Database</span>
            </button>
          </div>
        </form>
      )}

      {/* DETAILED FEATURES MATRIX */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono">
        <div className="p-4.5 bg-zinc-950/60 rounded-2xl border border-white/5 space-y-2 text-left">
          <h3 className="text-[11px] font-black uppercase text-white tracking-wider">1. High-Performance Hosting</h3>
          <span className="text-[8.5px] uppercase font-bold text-zinc-500 block border-b border-white/5 pb-1">
            £{quote?.hostingPrice?.toFixed(2)}/MO STANDARD RATE
          </span>
          <p className="text-[10.5px] text-zinc-400 leading-normal font-sans normal-case">
            Deployment of isolated containers guaranteeing elite page response speeds and complete isolation from standard server traffic leaks.
          </p>
        </div>

        <div className="p-4.5 bg-zinc-950/60 rounded-2xl border border-white/5 space-y-2 text-left">
          <h3 className="text-[11px] font-black uppercase text-white tracking-wider">2. Custom Domain & Mailboxes</h3>
          <span className="text-[8.5px] uppercase font-bold text-zinc-500 block border-b border-white/5 pb-1">
            £{quote?.emailPrice?.toFixed(2)}/MO SECURE CORPORATE EMAIL
          </span>
          <p className="text-[10.5px] text-zinc-400 leading-normal font-sans normal-case">
            Activation of custom corporate boxes (@yourdomain or @nexorax.co.uk) with modern secure parameters filtering.
          </p>
        </div>

        <div className="p-4.5 bg-zinc-950/60 rounded-2xl border border-white/5 space-y-2 text-left">
          <h3 className="text-[11px] font-black uppercase text-white tracking-wider">3. DNS & Domain Maintenance</h3>
          <span className="text-[8.5px] uppercase font-bold text-zinc-500 block border-b border-white/5 pb-1">
            £{quote?.domainPrice?.toFixed(2)}/MO INTEGRATION RATE
          </span>
          <p className="text-[10.5px] text-zinc-400 leading-normal font-sans normal-case">
            Provisioning and annual renewal of company domain records, DNS routing parameters, and automatic staging SSL handshakes.
          </p>
        </div>
      </div>

      {/* TRANSPARENT INVOICE BREAKDOWN CARD */}
      <div className="p-6 rounded-2xl bg-black/40 border border-zinc-900 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start font-mono">
        
        {/* Breakdown Values */}
        <div className="lg:col-span-7 space-y-4 text-left">
          <span className="text-[10px] text-yellow-500 uppercase block font-black tracking-widest uppercase">
            ❖ Itemized Launch Quote Invoice
          </span>

          <div className="space-y-2.5 text-xs text-zinc-400">
            <div className="flex justify-between border-b border-white/5 pb-1.5">
              <span>Standard Setup Fee (One-off):</span>
              <span className="line-through text-zinc-500">£{standardSetup.toFixed(2)}</span>
            </div>

            <div className="flex justify-between border-b border-white/5 pb-1.5">
              <span className="text-white font-bold">Introductory Price (One-off):</span>
              <span className="text-[#00ffff] font-black">
                {isApplied ? `£${promoSetup.toFixed(2)}` : 'Apply Bundle to Unlock'}
              </span>
            </div>

            {isApplied && (
              <div className="flex justify-between border-b border-white/5 pb-1.5 text-[#ffc526] font-bold">
                <span>You Save:</span>
                <span>£{youSaveVal.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between border-b border-white/5 pb-1.5">
              <span>Required Cloud Hosting (Monthly rate):</span>
              <span>£{quote?.hostingPrice?.toFixed(2)} {quote?.isVatRegistered ? 'excl. VAT' : ''}</span>
            </div>

            <div className="flex justify-between border-b border-white/5 pb-1.5">
              <span>Corporate Managed Email (Monthly rate):</span>
              <span>£{quote?.emailPrice?.toFixed(2)} {quote?.isVatRegistered ? 'excl. VAT' : ''}</span>
            </div>

            <div className="flex justify-between border-b border-white/5 pb-1.5">
              <span>Custom DNS & Domain Maintenance (Monthly):</span>
              <span>£{quote?.domainPrice?.toFixed(2)} {quote?.isVatRegistered ? 'excl. VAT' : ''}</span>
            </div>

            <div className="flex justify-between border-b border-white/5 pb-1.5 text-white font-bold pt-1">
              <span>Total Monthly Recurring Fees {quote?.isVatRegistered ? '(excl. VAT)' : ''}:</span>
              <span>£{quote?.totalMonthlyExclVat?.toFixed(2)}</span>
            </div>

            {quote?.isVatRegistered && (
              <div className="flex justify-between border-b border-white/5 pb-1.5 text-[11px] text-amber-500">
                <span>Standard UK VAT (calculated at {quote?.vatRate}%):</span>
                <span>£{quote?.vatAmount?.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between text-sm font-black text-white pt-2">
              <span className="uppercase tracking-wide">Total Monthly Recurring {quote?.isVatRegistered ? '(incl. VAT)' : ''}:</span>
              <span className="text-[#00ff00]">£{quote?.totalMonthlyInclVat?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Informative summary card */}
        <div className="lg:col-span-5 p-4.5 bg-zinc-950/40 rounded-xl border border-white/5 flex flex-col justify-between h-full min-h-[180px]">
          <div className="space-y-2 text-left">
            <span className="text-[8.5px] text-zinc-500 uppercase block font-bold leading-none">Subscription Terms</span>
            <h4 className="text-[11px] font-mono font-black text-white uppercase">{quote?.introductoryPeriod}</h4>
            <p className="text-[10px] text-zinc-450 leading-relaxed font-sans normal-case">
              {quote?.renewalPrice}
            </p>
          </div>

          <div className="pt-4 border-t border-white/5 text-zinc-500 text-[10px] font-sans normal-case space-y-1.5 leading-none">
            <div className="flex gap-1.5 items-center">
              <Check className="text-[#00ff00] shrink-0" size={10} />
              <span>Full onboarding support line configuration.</span>
            </div>
            <div className="flex gap-1.5 items-center">
              <Check className="text-[#00ff00] shrink-0" size={10} />
              <span>Cancel monthly subscription anytime without penalty.</span>
            </div>
          </div>
        </div>
      </div>

      {/* SECURITY AND LEAKAGE SAFEGUARDS NOTICE */}
      <div className="p-4 bg-zinc-950/40 border border-white/5 rounded-2xl flex items-start gap-3">
        <ShieldAlert size={15} className="text-[#e5c158] shrink-0 mt-0.5" />
        <div className="space-y-1 font-mono">
          <h4 className="text-[11px] font-mono text-[#e5c158] uppercase font-bold">Secure Quote Scope Guarding</h4>
          <p className="text-[9.5px] text-zinc-500 leading-relaxed font-sans normal-case">
            Disconnected rollout pricing parameters remain encapsulated inside this staging panel context. Staging configurations do not affect the main corporate checkout or public landing structures.
          </p>
        </div>
      </div>
    </div>
  );
};
